<template>
  <div> <!-- Or any other root element if needed, or just the v-card-title if it's always present -->
    <v-card-title v-if="title != null" class="pa-4">
      {{ title }}
      <div class="content-card-actions">
        <v-expansion-panels v-if="show" density="compact" variant="default" class="formatting-expander mr-2 mt-n1 py-2">
          <v-expansion-panel elevation="1" min-height="32px" bg-color="background">
            <v-expansion-panel-title class="text-caption py-0 px-2 ma-0" min-height="32px">
              Formatting
            </v-expansion-panel-title>
            <v-expansion-panel-text class="pa-0 mx-0">
              <v-checkbox v-model="addBackticks" label="Add Backticks" hide-details class="checkbox text-caption d-block mx-0"
                density="compact" />
              <v-radio-group class="formatting-options pa-0 mx-0" inline v-model="formatting" density="compact">
                <v-radio label="Raw" value="raw" class="text-caption" />
                <v-radio label="Basic" value="basic" class="text-caption" />
                <v-radio label="Full" value="full" class="text-caption" />
              </v-radio-group>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-btn color="primary" class="expand-btn mt-n1" variant="text"
          :icon="show ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="show = !show; $emit('contentUpdated')" />
      </div>
    </v-card-title>
    <v-expand-transition>
      <v-card-text :class="{ 'truncated-text': !show }">
        <div>
          <!-- Handle array content -->
          <div v-if="Array.isArray(content)">
            <div v-for="(item, index) in content" :key="`content-${index}`">
              <template v-if="item.type === 'text' && item.text">
                <Markdown :msg="item.text" />
              </template>
              <template v-if="item.type === 'image' && item.image">
                <img :src="`data:${item.image.mimeType};base64,${item.image.base64Data}`" class="content-image" />
              </template>
            </div>
          </div>
          <!-- Handle string content (existing logic) -->
          <div v-else-if="text !== ''">
            <p v-if="formatting !== 'full'" :class="formatting === 'basic' ? 'format-text' : ''">{{ text }}</p>
            <div v-if="formatting === 'full'">
              <v-expansion-panels density="compact" v-if="thinking !== ''" class="mb-4">
                <v-expansion-panel>
                  <v-expansion-panel-title class="text-overline thinking pa-2">Thinking</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <p class="text-caption">{{ thinking }}</p>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
              <div v-for="(chunk, index) in chunks" :key="index">
                <CodeBlock v-if="chunk.code" :code="chunk.text" :language="chunk.language" :lineStart="lineStart" />
                <Markdown v-else :msg="chunk.text"/>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-expand-transition>
  </div> <!-- Corresponding closing tag -->
</template>
<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { parseThinkingResponse, parseText, ResponseChunk } from '../../utils/general'
import CodeBlock from '../sub/CodeBlock.vue'
import Markdown from '../sub/Markdown.vue'

@Options({
  props: {
    title: String,
    content: [String, Array],
    expanded: [Boolean, String],
    lineStart: Number,
    streaming: Boolean
  },
  components: {
    CodeBlock,
    Markdown
  },
  emits: ['contentUpdated'],
  watch: {
    content() {
      (this as any).scheduleParse()
    },
    streaming(val: boolean) {
      const self = this as any
      if (val === false) {
        if (self.parseDebounceTimer != null) {
          window.clearTimeout(self.parseDebounceTimer)
          self.parseDebounceTimer = null
        }
        self.doParse()
      }
    }
  }
})
export default class FormattingCard extends Vue {
  public title!: string
  public content!: string | any[]
  public expanded!: boolean | string
  public show: boolean = true
  public formatting: string = 'full'
  public addBackticks: boolean = false
  public lineStart!: number
  public text: string = ''
  public thinking: string = ''
  public chunks: ResponseChunk[] = []
  parseText = parseText

  // Streaming/debounce control
  public streaming!: boolean
  private parseDebounceTimer: number | null = null
  private readonly parseDebounceMs: number = 60
  // Parse throttling heuristics to keep UI responsive during streaming
  private lastParsedLen: number = 0
  private lastFenceCount: number = 0
  private readonly minDeltaChars: number = 120

  private scheduleParse() {
    if (typeof this.content !== 'string') return
    const content = this.content ?? ''
    const fenceCount = (content.match(/```/g) || []).length

    if (this.streaming) {
      // Skip scheduling if the delta is tiny and structure hasn't changed
      if (Math.abs(content.length - this.lastParsedLen) < this.minDeltaChars && fenceCount === this.lastFenceCount) {
        return
      }
      if (this.parseDebounceTimer != null) window.clearTimeout(this.parseDebounceTimer)
      this.parseDebounceTimer = window.setTimeout(() => {
        this.parseDebounceTimer = null
        this.doParse()
      }, this.parseDebounceMs)
    } else {
      if (this.parseDebounceTimer != null) {
        window.clearTimeout(this.parseDebounceTimer)
        this.parseDebounceTimer = null
      }
      this.doParse()
    }
  }

  private doParse() {
    if (typeof this.content !== 'string') return
    const content = this.content ?? ''
    // Keep display text exactly as received (optionally wrapped by addBackticks)
    this.text = this.addBackticks ? '```\n' + content + '\n```' : content
    const stable = this.stableForParse(content)
    this.handleResponse(stable)
    // Update heuristics after a successful parse
    this.lastParsedLen = content.length
    this.lastFenceCount = (content.match(/```/g) || []).length
  }

  private stableForParse(raw: string): string {
    try {
      const fenceCount = (raw.match(/```/g) || []).length
      if (fenceCount % 2 === 1) {
        return raw + '\n```'
      }
    } catch (_e) {
      // no-op: if regex fails, fall back to raw
    }
    return raw
  }

  handleResponse(response: string) {
    const { thinking, chunks } = parseThinkingResponse(response)
    this.thinking = thinking
    this.chunks = chunks
  }

  // get text(): string {
  //   const content = this.content ?? ''
  //   return this.addBackticks ? '```\n' + content + '\n```' : content
  // }

  async mounted() {
    if (this.expanded == null) {
      // do nothing, use default value
    } else if (typeof this.expanded === 'boolean') {
      this.show = this.expanded
    } else if (this.expanded === 'true') {
      // manual expanded by default
      this.show = true
    } else if (this.expanded === 'false') {
      // manual collapsed by default
      this.show = false
    } else {
      // do nothing
    }

    // Initial parse
    if (typeof this.content === 'string') {
      this.scheduleParse()
    }
  }

}
</script>
<style lang="scss" scoped>
.content-image {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  margin-top: 8px;
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
}

.content-card-actions {
  position: absolute;
  right: 10px;
  top: 10px;
  width: auto; /* Adjusted for flexible width */
  display: flex; /* Added for alignment */
  align-items: flex-start; /* Align items to the top */

  .formatting-expander {
    flex-grow: 0; /* Prevent expander from growing */
    flex-shrink: 0; /* Prevent expander from shrinking */
    width: 190px; /* Adjusted width for the formatting panel */
    z-index: 1000; /* Ensure it's above other elements */
    // border: 1px solid rgb(var(--v-theme-border-color));
    border-radius: 4px;
  }

  .checkbox {
    display: inline-block;
    padding-top: 2px;
    vertical-align: top;
  }

  .formatting-options {
    display: inline-block;
    vertical-align: top;
  }
  .expand-btn {
    display: inline-block;
    margin-left: 8px; /* Add some space between expander and button */
    flex-shrink: 0; /* Ensure button does not shrink */
  }
}
.v-expansion-panel--active {
  & > .v-expansion-panel-title.thinking {
    min-height: 36px;
  }
}
.v-expansion-panel-title.thinking {
  min-height: 32px;
}

.formatting-expander .v-expansion-panel-title {
  min-height: 32px !important;
}

.truncated-text > div {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
